import { WaitingListLeadState } from "../types/enums";
import GenericInformationTable, { TableRow } from "components/ui/table/GenericInformationTable";
import { getModalTextsByProductType } from "../utils";

interface Props {
  firstName: string;
  lastName: string;
  productName: string;
  clientAvailability: string;
  requestZipCode: string;
  requestRegion: string;
  phoneNumber: string;
  waitingListLeadState: WaitingListLeadState;
  isClosedByClient: boolean;
  isPersonalProduct: boolean;
}

/**
 * WaitingListLeadBasicInformationTable displays key lead information using the GenericInformationTable.
 * It builds an array of table rows for fields such as the client's name, phone number, product need,
 * requested region, and best availability.
 */
function WaitingListLeadBasicInformationTable({
  firstName,
  lastName,
  productName,
  clientAvailability,
  requestRegion,
  requestZipCode,
  phoneNumber,
  waitingListLeadState,
  isClosedByClient,
  isPersonalProduct,
}: Readonly<Props>) {
  // Retrieve dynamic texts based on product type.
  const {
    approvedStateContactDetailsTableHeader,
    closedStateContactDetailsTableHeader,
    openCapacityStateProductionCriteriaDescription,
  } = getModalTextsByProductType(isPersonalProduct);

  // Determine the title to be used on the table.
  function getTableTitle(): string {
    return waitingListLeadState === WaitingListLeadState.Acquired
      ? approvedStateContactDetailsTableHeader
      : closedStateContactDetailsTableHeader;
  }

  // In this helper we decide whether to show the actual value or a placeholder.
  function isDataShowable(showIfRequestIsOpen: boolean): boolean {
    if (
      waitingListLeadState === WaitingListLeadState.Acquired ||
      (waitingListLeadState === WaitingListLeadState.Open &&
        !isClosedByClient &&
        showIfRequestIsOpen)
    ) {
      return true;
    }
    return false;
  }

  // Determine the title for the client name field.
  function getClientNameTitle(): string {
    if (waitingListLeadState === WaitingListLeadState.Acquired) {
      if (firstName && lastName) {
        return "Name, Vorname:";
      } else if (firstName && firstName.trim() !== "") {
        return "Vorname:";
      } else if (lastName && lastName.trim() !== "") {
        return "Name:";
      }
    }
    return "Name, Vorname:";
  }

  // Return the client name (or a placeholder when not allowed).
  function getClientName(): string {
    if (waitingListLeadState === WaitingListLeadState.Acquired) {
      return firstName && lastName ? `${lastName}, ${firstName}` : firstName || lastName || "N/A";
    }
    return "******";
  }

  function getHeaderColor(){
    return   waitingListLeadState ===WaitingListLeadState.Acquired && !isClosedByClient? "green-box" : "blue-box";
  }

  const rows: TableRow[] = [
    {
      fieldName: getClientNameTitle(),
      value: getClientName(),
      isDataShowable: waitingListLeadState === WaitingListLeadState.Acquired
    },
    {
      fieldName: "Telefonnummer:",
      value: phoneNumber,
      isDataShowable:isDataShowable(false)
      
    },
    {
      fieldName: "Bedarf:",
      value: productName,
      isDataShowable:isDataShowable(false)
    },
    {
      fieldName: "Bedarfsort:",
      value: `${requestRegion}, ${requestZipCode}`,
      isDataShowable:isDataShowable(false)
    },
    {
      fieldName: "Beste Erreichbarkeit:",
      value: clientAvailability,
      isDataShowable:isDataShowable(false)
    },
  ];

  return (
    <div>
      {waitingListLeadState === WaitingListLeadState.Acquired && !isClosedByClient && (
        <h3 className="text-blue">Rufen Sie jetzt an:</h3>
      )}
      <GenericInformationTable title={getTableTitle()} rows={rows} headerColor={getHeaderColor()} />
      
      {waitingListLeadState === WaitingListLeadState.Open && !isClosedByClient && (
        <p
          className="text-black"
          dangerouslySetInnerHTML={{ __html: openCapacityStateProductionCriteriaDescription }}
        ></p>
      )}
    </div>
  );
}

export default WaitingListLeadBasicInformationTable;
